import { supabase } from "./supabase";
import {
  Item,
  ItemCategory,
  ItemStatus,
  Coordinates,
  CategorySelection,
} from "./types";
import type { Database } from "./database.types";
import { parseSellerReviewFeedback } from "./seller-review-feedback";
import { getItemImages, getItemImagesByItemIds } from "./serverImageUtils";
import {
  deserializeHomepageRecentlySoldItems,
  type HomepageRecentlySoldItemDto,
} from "./items/fetchHomepageRecentlySoldItems";
import {
  deserializeRecentlyConfirmedItems,
  type RecentlyConfirmedItemDto,
} from "./items/fetchRecentlyConfirmedItems";
import logger from "./utils/logger";
import { getStateLocationKeywords } from "./locationHierarchy";
import type { PostgrestError } from "@supabase/supabase-js";

// Helper function to handle Supabase errors
// Accepts PostgrestError which is the actual type returned by Supabase queries
const handleSupabaseError = (error: PostgrestError | null, context: string) => {
  if (!error) return;

  // PostgrestError has code and message properties
  // Check if it's an authentication error using the code property
  const errorCode = error.code || "";
  if (
    errorCode === "PGRST301" ||
    error.message?.includes("401") ||
    error.message?.includes("unauthorized")
  ) {
    logger.error(`Authentication error in ${context}:`, error);
    // Log the user out to prevent infinite error loops
    try {
      supabase.auth.signOut();
    } catch (e) {
      logger.error("Error signing out:", e);
    }
  } else {
    logger.error(`Error in ${context}:`, error);
  }
};

// Use actual database type for type safety
type SupabaseItem = Database["public"]["Tables"]["items"]["Row"];
type HomepageItemRow = Pick<
  SupabaseItem,
  | "id"
  | "title"
  | "description"
  | "defects"
  | "price"
  | "quantity"
  | "category"
  | "subcategory"
  | "location"
  | "coordinates"
  | "seller_id"
  | "status"
  | "seller_review_feedback"
  | "created_at"
  | "updated_at"
  | "seller_availability_confirmed_at"
>;

const HOMEPAGE_ITEM_COLUMNS =
  "id,title,description,defects,price,quantity,category,subcategory,location,coordinates,seller_id,status,seller_review_feedback,created_at,updated_at,seller_availability_confirmed_at";

// All valid category values (must match ItemCategory in types.ts)
const VALID_CATEGORIES: readonly ItemCategory[] = [
  "baby",
  "bedding",
  "decor",
  "digital_wears",
  "electronics",
  "fashion",
  "fitness",
  "furniture",
  "gaming",
  "kitchen",
  "lighting",
  "outdoor",
  "personal_computers",
  "phones_tabs",
  "power_solutions",
  "tools",
  "other",
] as const;

// All valid status values (now synced between DB and domain)
const VALID_STATUSES: readonly ItemStatus[] = [
  // Submission & Review statuses
  "new_submission",
  "pending_review",
  "needs_user_action",
  "resubmitted",
  "approved",
  "rejected",
  // Fulfillment & Settlement statuses
  "payment_received",
  "item_collected",
  "payment_processed",
  "sale_complete",
  "sale_failed",
  // Special statuses
  "archived",
  // Legacy statuses (kept for backward compatibility with existing data, but not selectable in UI)
  "pending",
  "sold",
  "sold_elsewhere",
  "payment_in_process",
  "draft",
  "picked_up",
] as const;

const POWER_SOLUTIONS_SUBCATEGORY_IDS = new Set([
  "generator",
  "inverter",
  "inverter-battery",
]);

function toItemCategory(
  category: string | null,
  subcategory?: string | null,
): ItemCategory {
  if (
    category === "electronics" &&
    subcategory &&
    POWER_SOLUTIONS_SUBCATEGORY_IDS.has(subcategory)
  ) {
    return "power_solutions";
  }

  const resolvedCategory = (category || "other") as ItemCategory;
  if (!VALID_CATEGORIES.includes(resolvedCategory)) {
    logger.warn(`Invalid category "${category}", defaulting to "other"`);
    return "other";
  }
  return resolvedCategory;
}

function toItemStatus(status: string | null): ItemStatus {
  const resolvedStatus = (status || "new_submission") as ItemStatus;
  if (!VALID_STATUSES.includes(resolvedStatus)) {
    logger.warn(`Invalid status "${status}", defaulting to "new_submission"`);
    return "new_submission";
  }
  return resolvedStatus;
}

function toCoordinates(
  value: SupabaseItem["coordinates"],
): Coordinates | undefined {
  if (!value) return undefined;

  try {
    const coords = value as { lat?: unknown; lng?: unknown };
    if (typeof coords.lat === "number" && typeof coords.lng === "number") {
      return { lat: coords.lat, lng: coords.lng };
    }
  } catch {
    // Invalid coordinates are ignored for preview lists.
  }

  return undefined;
}

function sortItemImages(images: Awaited<ReturnType<typeof getItemImages>>) {
  return [...images].sort((a, b) => {
    if (a.isMain && !b.isMain) return -1;
    if (!a.isMain && b.isMain) return 1;
    return a.position - b.position;
  });
}

async function mapHomepageRowsToItems(
  rows: HomepageItemRow[],
): Promise<Item[]> {
  const imagesByItemId = await getItemImagesByItemIds(
    rows.map((item) => item.id),
  );

  return rows
    .filter((item) => item.id && item.title && item.seller_id)
    .map((item) => {
      const sortedImages = sortItemImages(imagesByItemId.get(item.id) ?? []);
      return {
        id: item.id,
        title: item.title,
        description: item.description || undefined,
        defects: item.defects || undefined,
        price: item.price,
        quantity: item.quantity || 1,
        category: toItemCategory(item.category, item.subcategory),
        subcategory: item.subcategory || "",
        images: sortedImages.map((img) => img.url),
        imageDetails: sortedImages,
        location: item.location || "Lagos",
        coordinates: toCoordinates(item.coordinates),
        sellerId: item.seller_id,
        status: toItemStatus(item.status),
        sellerReviewFeedback: parseSellerReviewFeedback(
          item.seller_review_feedback,
        ),
        createdAt: item.created_at ? new Date(item.created_at) : new Date(),
        updatedAt: item.updated_at ? new Date(item.updated_at) : new Date(),
        sellerAvailabilityConfirmedAt: item.seller_availability_confirmed_at
          ? new Date(item.seller_availability_confirmed_at)
          : null,
      };
    });
}

// Convert Supabase item to our Item type
// Handles null values from database and converts to domain types
export const mapSupabaseItemToItem = async (
  item: SupabaseItem,
): Promise<Item> => {
  try {
    // Validate required fields
    if (!item.id || !item.title || !item.seller_id) {
      throw new Error("Invalid item: missing required fields");
    }

    // Fetch detailed images for this item
    const images = await getItemImages(item.id);

    // Ensure main image is first in the array by sorting
    // Images are already sorted by position (main image has position 0)
    // This is a safeguard in case the position field is not correctly set
    const sortedImages = [...images].sort((a, b) => {
      // If one is the main image, it should come first
      if (a.isMain && !b.isMain) return -1;
      if (!a.isMain && b.isMain) return 1;
      // Otherwise keep the original position-based order
      return a.position - b.position;
    });

    // Fetch pickup availability for this item
    const { data: pickupData, error: pickupError } = await supabase
      .from("pickup_availability")
      .select("*")
      .eq("item_id", item.id);

    if (pickupError) {
      handleSupabaseError(pickupError, "fetching pickup availability");
    }

    const category = toItemCategory(item.category, item.subcategory);

    // Validate and cast status (nullable in DB but required in domain)
    // Enums are now synced, so direct casting is safe
    let status: ItemStatus = (item.status || "new_submission") as ItemStatus;
    if (!VALID_STATUSES.includes(status)) {
      logger.warn(
        `Invalid status "${item.status}", defaulting to "new_submission"`,
      );
      status = "new_submission"; // Fallback to 'new_submission' if somehow an invalid status exists
    }

    // Handle coordinates (nullable JSON in DB)
    let coordinates: Coordinates | undefined;
    if (item.coordinates) {
      try {
        const coords = item.coordinates as
          | { lat: number; lng: number }
          | Coordinates;
        if (typeof coords === "object" && "lat" in coords && "lng" in coords) {
          coordinates = { lat: coords.lat, lng: coords.lng };
        }
      } catch {
        // Invalid coordinates, leave as undefined
        logger.warn(`Invalid coordinates for item ${item.id}`);
      }
    }

    // Handle dates (nullable in DB but required in domain)
    const createdAt = item.created_at ? new Date(item.created_at) : new Date();
    const updatedAt = item.updated_at ? new Date(item.updated_at) : new Date();
    const sellerAvailabilityConfirmedAt = item.seller_availability_confirmed_at
      ? new Date(item.seller_availability_confirmed_at)
      : null;

    return {
      id: item.id,
      title: item.title,
      description: item.description || undefined,
      defects: item.defects || undefined,
      price: item.price,
      quantity: item.quantity || 1, // Default to 1 if not specified
      category,
      subcategory: item.subcategory || "",
      images: sortedImages.map((img) => img.url),
      imageDetails: sortedImages,
      location: item.location || "Lagos",
      coordinates,
      sellerId: item.seller_id,
      status,
      sellerReviewFeedback: parseSellerReviewFeedback(
        item.seller_review_feedback,
      ),
      createdAt,
      updatedAt,
      sellerAvailabilityConfirmedAt,
      pickupAvailability:
        pickupData?.map((pickup) => ({
          dayOfWeek: pickup.day_of_week,
          startHour: pickup.start_hour,
          endHour: pickup.end_hour,
        })) || [],
    };
  } catch (error) {
    logger.error("Error in mapSupabaseItemToItem:", error);
    // Return a minimal valid item to prevent UI errors
    const { data: imageData } = await supabase
      .from("item_images")
      .select("url, is_main, position")
      .eq("item_id", item.id)
      .order("position", { ascending: true });

    const imageUrls = imageData?.length
      ? // Sort ensuring main image is first if position sorting fails
        [...imageData]
          .sort((a, b) => {
            if (a.is_main && !b.is_main) return -1;
            if (!a.is_main && b.is_main) return 1;
            const aPos = a.position ?? 0;
            const bPos = b.position ?? 0;
            return aPos - bPos;
          })
          .map((img) => img.url)
      : [];

    // Handle nullable fields with defaults
    const category = (item.category || "other") as ItemCategory;
    const status = (item.status || "new_submission") as ItemStatus;
    const subcategory = item.subcategory || "";

    // Handle coordinates safely
    let coordinates: Coordinates | undefined;
    if (item.coordinates) {
      try {
        const coords = item.coordinates as
          | { lat: number; lng: number }
          | Coordinates;
        if (
          typeof coords === "object" &&
          coords !== null &&
          "lat" in coords &&
          "lng" in coords
        ) {
          coordinates = { lat: coords.lat, lng: coords.lng };
        }
      } catch {
        // Invalid coordinates, leave as undefined
      }
    }

    return {
      id: item.id,
      title: item.title,
      description: item.description || undefined,
      defects: item.defects || undefined,
      price: item.price,
      quantity: item.quantity || 1, // Default to 1 if not specified
      category,
      subcategory,
      images: imageUrls,
      location: item.location || "Lagos",
      coordinates,
      sellerId: item.seller_id,
      status,
      sellerReviewFeedback: parseSellerReviewFeedback(
        item.seller_review_feedback,
      ),
      createdAt: item.created_at ? new Date(item.created_at) : new Date(),
      updatedAt: item.updated_at ? new Date(item.updated_at) : new Date(),
      pickupAvailability: [],
    };
  }
};

/**
 * Fetch approved item rows, prioritizing the buyer's state (R5(b)).
 *
 * WHY: a flat "newest N approved nationwide" fetch can starve a quiet local area —
 * its in-state inventory that sits past the newest-N window is never fetched, so the
 * buyer sees out-of-state spillover even when nearer items exist in the DB. This
 * fetches the buyer's in-state newest-N first, then FILLS the remainder with the
 * global newest-N (deduped) so out-of-state spillover still works for thin states.
 *
 * When `userState` is null/unknown, behavior is identical to the legacy flat fetch.
 * The ILIKE filter is built from the internal, curated {@link getStateLocationKeywords}
 * owner (no user input), so there is no injection surface.
 *
 * Returns raw rows; callers own the row → domain mapping and their `columns` set.
 */
async function fetchApprovedRowsStatePrioritized<Row extends { id: string }>(
  columns: string,
  limit: number,
  userState: string | null,
): Promise<Row[]> {
  const newestGlobal = () =>
    supabase
      .from("items")
      .select(columns)
      .eq("status", "approved")
      .order("created_at", { ascending: false })
      .limit(limit)
      .returns<Row[]>();

  const keywords = userState ? getStateLocationKeywords(userState) : [];

  // No usable state signal → preserve legacy global newest-N behavior exactly.
  if (keywords.length === 0) {
    const { data, error } = await newestGlobal();
    if (error) {
      handleSupabaseError(error, "fetchApprovedRowsStatePrioritized");
      return [];
    }
    return data ?? [];
  }

  // 1) In-state newest-N (can reach older-than-global rows the flat window missed).
  const orFilter = keywords
    .map((keyword) => `location.ilike.%${keyword}%`)
    .join(",");
  const { data: inStateData, error: inStateError } = await supabase
    .from("items")
    .select(columns)
    .eq("status", "approved")
    .or(orFilter)
    .order("created_at", { ascending: false })
    .limit(limit)
    .returns<Row[]>();

  if (inStateError) {
    // In-state query failed — fall back to the global fetch so the surface still fills.
    handleSupabaseError(
      inStateError,
      "fetchApprovedRowsStatePrioritized:inState",
    );
    const { data, error } = await newestGlobal();
    if (error) {
      handleSupabaseError(error, "fetchApprovedRowsStatePrioritized:fallback");
      return [];
    }
    return data ?? [];
  }

  const inStateRows = inStateData ?? [];

  // Enough in-state inventory → no spillover needed.
  if (inStateRows.length >= limit) {
    return inStateRows;
  }

  // 2) Fill the remainder with global newest-N so thin states still show spillover.
  const { data: globalData, error: globalError } = await newestGlobal();
  if (globalError) {
    handleSupabaseError(globalError, "fetchApprovedRowsStatePrioritized:fill");
    return inStateRows;
  }

  return mergeUniqueRows(inStateRows, globalData ?? [], limit);
}

/**
 * Merge two row lists, `primary` first, deduped by id, capped at `limit`.
 * Pure helper (exported for testing) — the primary (in-state) rows are always
 * preserved; fill rows only occupy the leftover slots.
 */
export function mergeUniqueRows<Row extends { id: string }>(
  primary: Row[],
  fill: Row[],
  limit: number,
): Row[] {
  const seen = new Set(primary.map((row) => row.id));
  const merged = [...primary];
  for (const row of fill) {
    if (merged.length >= limit) break;
    if (seen.has(row.id)) continue;
    seen.add(row.id);
    merged.push(row);
  }
  return merged;
}

// Get approved items with optional limit
// Default limit of 50 to prevent performance issues when fetching all items
export const getApprovedItems = async (
  limit?: number,
  userState: string | null = null,
): Promise<Item[]> => {
  try {
    // State-prioritized path (R5(b)): only when a limit is set (the market/homepage
    // browse). Unbounded callers keep the legacy full-table newest-first fetch.
    if (limit && limit > 0) {
      const rows = await fetchApprovedRowsStatePrioritized<SupabaseItem>(
        "*",
        limit,
        userState,
      );
      if (rows.length === 0) return [];
      return Promise.all(rows.map(mapSupabaseItemToItem));
    }

    // Only show approved items (items disappear once payment is received)
    // Sold items are excluded from buyer-facing market lists.
    const query = supabase
      .from("items")
      .select("*")
      .eq("status", "approved")
      .order("created_at", { ascending: false }); // Order by newest first

    const { data, error } = await query;

    if (error) {
      handleSupabaseError(error, "getApprovedItems");
      return [];
    }

    if (!data || data.length === 0) {
      return [];
    }

    logger.log(
      `Found ${data.length} approved items from database${limit ? ` (limited to ${limit})` : ""}`,
    );

    // Map each item and return
    const items = await Promise.all(data.map(mapSupabaseItemToItem));
    return items;
  } catch (error) {
    console.error("Error in getApprovedItems:", error);
    return [];
  }
};

// Get approved homepage preview items without detail-only relations like pickup availability.
export const getHomepageApprovedItems = async (
  limit: number = 60,
  userState: string | null = null,
): Promise<Item[]> => {
  try {
    // State-prioritized fetch (R5(b)) so a quiet local area still surfaces its own
    // in-state inventory before out-of-state spillover. Falls back to global
    // newest-N when the buyer's state is unknown.
    const rows = await fetchApprovedRowsStatePrioritized<HomepageItemRow>(
      HOMEPAGE_ITEM_COLUMNS,
      limit,
      userState,
    );

    if (!rows.length) {
      return [];
    }

    return mapHomepageRowsToItems(rows);
  } catch (error) {
    console.error("Error in getHomepageApprovedItems:", error);
    return [];
  }
};

// Get items by location
export const getItemsByLocation = async (location: string): Promise<Item[]> => {
  try {
    // Only show approved items (items disappear once payment is received)
    const { data, error } = await supabase
      .from("items")
      .select("*")
      .eq("status", "approved")
      .ilike("location", `%${location}%`);

    if (error) {
      handleSupabaseError(error, "getItemsByLocation");
      return [];
    }

    if (!data || data.length === 0) {
      return [];
    }

    const items = await Promise.all(data.map(mapSupabaseItemToItem));
    return items;
  } catch (error) {
    console.error("Error in getItemsByLocation:", error);
    return [];
  }
};

// Get items by category
export const getItemsByCategory = async (
  categorySelection: CategorySelection,
): Promise<Item[]> => {
  try {
    // Only show approved items (items disappear once payment is received)
    let query = supabase
      .from("items")
      .select("*")
      .eq("status", "approved")
      .eq("category", categorySelection.categoryId);

    // When subcategory is selected, filter by it; otherwise show all items in the category
    if (categorySelection.subcategoryId) {
      query = query.eq("subcategory", categorySelection.subcategoryId);
    }

    const { data, error } = await query;

    if (error) {
      handleSupabaseError(error, "getItemsByCategory");
      return [];
    }

    if (!data || data.length === 0) {
      return [];
    }

    const items = await Promise.all(data.map(mapSupabaseItemToItem));
    return items;
  } catch (error) {
    console.error("Error in getItemsByCategory:", error);
    return [];
  }
};

// Search items by query (searches in title and description)
// Uses term-based matching: "75 Smart TV" matches "75 Inches Smart TV" (all terms must appear)
function escapeForIlike(value: string): string {
  return value.replace(/\\/g, "\\\\").replace(/%/g, "\\%").replace(/_/g, "\\_");
}

async function getItemIdsMatchingTerm(term: string): Promise<Set<string>> {
  const escaped = escapeForIlike(term.trim());
  if (!escaped) return new Set();
  const pattern = `%${escaped}%`;
  const quoted = `"${pattern.replace(/"/g, '\\"')}"`;
  const { data, error } = await supabase
    .from("items")
    .select("id")
    .eq("status", "approved")
    .or(`title.ilike.${quoted},description.ilike.${quoted}`)
    .limit(500);

  if (error) {
    handleSupabaseError(error, "getItemIdsMatchingTerm");
    return new Set();
  }
  return new Set((data ?? []).map((row) => row.id));
}

export async function getItemsByIds(ids: string[]): Promise<Item[]> {
  if (ids.length === 0) return [];
  const { data, error } = await supabase
    .from("items")
    .select("*")
    .eq("status", "approved")
    .in("id", ids);

  if (error) {
    handleSupabaseError(error, "getItemsByIds");
    return [];
  }
  if (!data?.length) return [];
  const items = await Promise.all(data.map(mapSupabaseItemToItem));
  // Preserve order of ids (intersection order)
  const idToOrder = new Map(ids.map((id, i) => [id, i]));
  items.sort((a, b) => (idToOrder.get(a.id) ?? 0) - (idToOrder.get(b.id) ?? 0));
  return items;
}

export const searchItems = async (query: string): Promise<Item[]> => {
  try {
    const trimmed = query.trim();
    if (!trimmed) return [];

    const terms = trimmed
      .split(/\s+/)
      .map((t) => t.trim())
      .filter(Boolean);
    if (terms.length === 0) return [];

    if (terms.length === 1) {
      const ids = await getItemIdsMatchingTerm(terms[0]!);
      return getItemsByIds([...ids]);
    }

    const idSets = await Promise.all(
      terms.map((term) => getItemIdsMatchingTerm(term)),
    );
    const intersection = idSets.reduce((acc, set) => {
      const next = new Set<string>();
      acc.forEach((id) => {
        if (set.has(id)) next.add(id);
      });
      return next;
    });
    return getItemsByIds([...intersection]);
  } catch (error) {
    logger.error("Error in searchItems:", error);
    return [];
  }
};

// Relaxed search for "related" results when exact/term search returns few or no results.
// Uses a subset of terms (e.g. drop the first) so "75 Smart TV" -> "Smart TV".
export const getRelatedSearchItems = async (
  query: string,
  excludeIds: string[],
  limit: number = 12,
): Promise<Item[]> => {
  try {
    const terms = query
      .trim()
      .split(/\s+/)
      .map((t) => t.trim())
      .filter(Boolean);
    if (terms.length === 0) return [];

    const excludeSet = new Set(excludeIds);
    const relaxedQuery =
      terms.length > 1 ? terms.slice(1).join(" ") : (terms[0] ?? "");
    if (!relaxedQuery) return [];

    const candidates = await searchItems(relaxedQuery);
    const filtered = candidates.filter((item) => !excludeSet.has(item.id));
    return filtered.slice(0, limit);
  } catch (error) {
    logger.error("Error in getRelatedSearchItems:", error);
    return [];
  }
};

// Get an item by its ID
export const getItemById = async (id: string): Promise<Item | null> => {
  try {
    // Use maybeSingle() instead of single() to handle the case where the item might not exist
    const { data, error } = await supabase
      .from("items")
      .select("*")
      .eq("id", id)
      .maybeSingle();

    if (error) {
      handleSupabaseError(error, "getItemById");
      return null;
    }

    if (!data) {
      console.log(`No item found with ID: ${id}`);
      return null;
    }

    // Map the item and return
    const item = await mapSupabaseItemToItem(data);
    return item;
  } catch (error) {
    console.error("Error in getItemById:", error);
    return null;
  }
};

// Mark an item as picked up (set status to sale_complete).
/** @deprecated Use `confirmBuyerPickup(transactionId)` from `@/lib/transactionAPI`. */
export const markItemAsPickedUp = async (
  itemId: string,
  transactionId: string,
): Promise<boolean> => {
  void itemId;
  const { confirmBuyerPickup } = await import("./transactionAPI");
  return confirmBuyerPickup(transactionId);
};

// Get sold homepage preview items without detail-only relations like pickup availability.
// Uses server API (service role) because RLS only grants public SELECT on `approved`.
export const getHomepageRecentlySoldItems = async (
  limit: number = 20,
): Promise<Item[]> => {
  try {
    const safeLimit = Math.min(Math.max(limit, 1), 50);
    const response = await fetch(
      `/api/items/recently-sold?limit=${encodeURIComponent(String(safeLimit))}`,
      { method: "GET" },
    );

    if (!response.ok) {
      console.error(
        "getHomepageRecentlySoldItems API error:",
        response.status,
        response.statusText,
      );
      return [];
    }

    const payload = (await response.json()) as {
      items?: HomepageRecentlySoldItemDto[];
    };

    if (!payload.items?.length) {
      return [];
    }

    return deserializeHomepageRecentlySoldItems(payload.items);
  } catch (error) {
    console.error("Error in getHomepageRecentlySoldItems:", error);
    return [];
  }
};

export const getRecentlyConfirmedItems = async (
  limit: number = 48,
  windowDays: number = 7,
): Promise<Item[]> => {
  try {
    const safeLimit = Math.min(Math.max(limit, 1), 60);
    const safeWindow = Math.min(Math.max(windowDays, 1), 365);
    const response = await fetch(
      `/api/items/recently-confirmed?limit=${encodeURIComponent(String(safeLimit))}&windowDays=${encodeURIComponent(String(safeWindow))}`,
      { method: "GET" },
    );

    if (!response.ok) {
      console.error(
        "getRecentlyConfirmedItems API error:",
        response.status,
        response.statusText,
      );
      return [];
    }

    const payload = (await response.json()) as {
      items?: RecentlyConfirmedItemDto[];
    };

    if (!payload.items?.length) {
      return [];
    }

    return deserializeRecentlyConfirmedItems(payload.items);
  } catch (error) {
    console.error("Error in getRecentlyConfirmedItems:", error);
    return [];
  }
};
